import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from '@emotion/styled';
import { SimpleSelector } from 'ui_system/forms/SimpleSelector';
import OnlineMeetingModal from 'ui_system/forms/onlineMeetingModal';
import { lowercase, titleize } from 'lib/string_utils';
import { Field, Form } from 'formik';

import {
  createExperienceMeetingInfo,
  updateExperienceMeetingInfo,
  updateTourStepForm,
  setExperienceLength,
} from 'data_system/resource_portal/actions/experienceActions';

import { toggleShowResourceMeetingModal } from 'data_system/resource_portal/actions/resourceActions';

import FormSubmitButton from 'views/resource_portal/routes/experiences/ui/FormSubmitButton';
import { Grid } from 'views/anyguide/messages/styles';
import ErrorFocus from 'views/anyguide/resources/components/ErrorFocus';
import { translateWithObject } from 'services/translation';

import {
  FormContainer,
  FieldSection,
  Label,
  SubLabel,
  SubLabelWarning,
  Actions,
  Error,
  Text,
  DividingLine,
} from 'ui_system/forms/FormStyles';
import OnlineExperienceDetail from './onlineExperienceDetail';
import LocationSelector from '../../ui/LocationSelector';
import PriceInput from '../../ui/PriceInput';

import {
  DEFAULT_ONLINE_MAX_CAPACITY,
  DEFAULT_OFFLINE_MAX_CAPACITY,
} from './constants';

const LengthSection = styled(Grid)`
  width: 100%;
  grid-gap: 20px;
`;

const InnerLengthSection = styled(Grid)`
  grid-gap: 20px;
`;

const generateOptions = (args) => {
  const res = [];
  let s = args.start;
  [...Array(args.iterations)].map((_, i) => {
    res.push({ value: `${s}`, label: `${s}` });
    s += 1;
  });
  return res;
};

// ========== Prop helpers
// Form content
const DetailsForm = (props) => {
  const t = translateWithObject(props.partner.translations);

  useEffect(() => {
    props.updateTourStepForm(null);
  }, []);

  const setFieldValue = (name, value) => {
    const { tourStepForm, updateTourStepForm, setFieldValue } = props;
    if (!tourStepForm) updateTourStepForm(props);
    setFieldValue(name, value);
  };

  const setExperienceLength = (name, value) => {
    props.setExperienceLength(parseFloat(value));
    setFieldValue(name, value);
  };

  const locationAddress = ({ custom_id, name, address } = {}) => {
    if (custom_id && name) {
      return `${custom_id} ${name}`;
    }
    if (address) {
      return address;
    }
    return 'All locations';
  };

  const renderLengthSection = () => {
    const { errors, experience } = props;
    const { hasFutureSchedule } = experience;
    return (
      <React.Fragment>
        <Label>{t('LENGTH_OF_THE_CLASS')}</Label>
        <SubLabel>
          {t('CLASSES_OF_1_3_HOURS_ARE_RECOMMENDED_FOR_MAXIMUM_ENGAGEMENT')}
        </SubLabel>
        <LengthSection gridGap={15} gridTemplateColumns="1fr 1fr">
          <InnerLengthSection gridTemplateColumns="60% 40%">
            <Field
              name="lengthHours"
              options={generateOptions({ start: 0, iterations: 5 })}
              component={SimpleSelector}
              onChange={setFieldValue}
              isDisabled={hasFutureSchedule}
            />
            <Text
              small
              style={{
                alignSelf: 'center',
                margin: 0,
                textTransform: 'capitalize',
              }}
            >
              {t('HOURS')}
            </Text>
          </InnerLengthSection>
          <InnerLengthSection gridTemplateColumns="60% 40%">
            <Field
              name="lengthMinutes"
              options={[
                { label: '00', value: 0 },
                { label: '15', value: 15 },
                { label: '30', value: 30 },
                { label: '45', value: 45 },
              ]}
              component={SimpleSelector}
              onChange={setFieldValue}
              isDisabled={hasFutureSchedule}
            />
            <Text
              small
              style={{
                alignSelf: 'center',
                margin: 0,
                textTransform: 'capitalize',
              }}
            >
              {lowercase(t('MINUTES'))}
            </Text>
          </InnerLengthSection>
          {errors.lengthMinutes && (
            <Error style={{ gridColumn: 'span 2' }} noMargin>
              {errors.lengthMinutes}
            </Error>
          )}
        </LengthSection>
        <SubLabelWarning>
          {t(
            'THE_LENGTH_CANNOT_BE_CHANGED_WHEN_THERE_ARE_TIMESLOTS_CURRENTLY_SCHEDULED'
          )}
        </SubLabelWarning>
      </React.Fragment>
    );
  };

  const handleDetailFormModalValidation = (values) => {
    const { setFieldValue, setFieldTouched, validateForm, errors } = props;
    const { vendorMeetingUrl } = values;
    setFieldValue('vendorMeetingUrl', vendorMeetingUrl);
    setFieldTouched('vendorMeetingUrl', true);
    if (errors.vendorMeetingUrl) {
      validateForm();
    }
  };

  const handleModalSubmit = (values) => {
    const {
      createExperienceMeetingInfo,
      updateExperienceMeetingInfo,
      experience,
      user,
    } = props;
    const apiValues = {
      ...values,
      meetableId: experience.id,
      resourceId: user.data && user.data.id,
      meetingVendorId: values.meetingVendor.id,
    };
    values.id
      ? updateExperienceMeetingInfo(apiValues)
      : createExperienceMeetingInfo(apiValues);
    handleDetailFormModalValidation(values);
  };

  const {
    dirty,
    errors,
    tourStepForm,
    experience,
    brand,
    i18n,
    partner,
    experienceUpdating,
    showResourceMeetingModal,
    toggleShowResourceMeetingModal,
    onlineMeeting,
    onlineMeetingVendors,
  } = props;
  const { hasFutureSchedule, isFree, isOnline, templateMaxCapacity } =
    experience;
  let resourcePortalCurrency = null;
  if (partner.id === 42446) {
    resourcePortalCurrency = 'CAD';
  } else {
    resourcePortalCurrency = 'USD';
  }

  const closeModal = () => {
    if (!experienceUpdating) {
      toggleShowResourceMeetingModal(false);
    }
  };

  return (
    <>
      <Form>
        <ErrorFocus />
        <FormContainer>
          {isOnline ? (
            <OnlineExperienceDetail
              t={t}
              meetingInformation={onlineMeeting}
              error={errors.vendorMeetingUrl}
            />
          ) : (
            <FieldSection>
              <Label>{t(`${t('LOCATION_NAME_SINGULAR')}_LOCATION`)}</Label>
              <SubLabel>
                {t('PLEASE_SELECT_THE_STORE_YOU_D_LIKE_TO_TEACH_THIS_CLASS_AT')}
              </SubLabel>
              <Field
                name="location"
                component={LocationSelector}
                onChange={setFieldValue}
                isDisabled={hasFutureSchedule}
                defaultLabel={t(`SELECT_A_${t('STORE')}`)}
              />
              {hasFutureSchedule && (
                <SubLabelWarning>
                  {t(
                    'LOCATION_CANNOT_BE_CHANGED_WHEN_THERE_ARE_TIMESLOTS_CURRENTLY_SCHEDULED'
                  )}
                </SubLabelWarning>
              )}
              {errors.location && <Error>{errors.location}</Error>}
            </FieldSection>
          )}
          <DividingLine marginbottom />
          <FieldSection>{renderLengthSection()}</FieldSection>
          <DividingLine marginbottom />
          <FieldSection>
            <Label>{`${t('HOW_MANY_PEOPLE_CAN_ATTEND_THIS_CLASS')}?`}</Label>
            {!isOnline && (
              <SubLabel>
                {t('SUBJECT_TO_AVAILABLE_SPACE_AT_THE_STORE')}
              </SubLabel>
            )}
            <div style={{ width: 180 }}>
              <Field
                name="maxCapacity"
                options={generateOptions({
                  start: 1,
                  iterations:
                    templateMaxCapacity ||
                    (isOnline
                      ? DEFAULT_ONLINE_MAX_CAPACITY
                      : DEFAULT_OFFLINE_MAX_CAPACITY),
                })}
                component={SimpleSelector}
                onChange={setFieldValue}
              />
              {errors.maxCapacity && (
                <Error noMargin>{errors.maxCapacity}</Error>
              )}
            </div>
          </FieldSection>
          <DividingLine marginbottom />
          {isFree ? (
            <FieldSection>
              <Label>{`${t('CLASS_PRICE_PER_STUDENT')}`}</Label>
              <Text>{t('FREE')}</Text>
            </FieldSection>
          ) : (
            <>
              <FieldSection>
                <Label>
                  {`${t(
                    'CLASS_PRICE_PER_STUDENT'
                  )} (${resourcePortalCurrency})`}
                </Label>
                <Text small light>
                  {`${t('PRICE_OF_CLASS_DOES_NOT_INCLUDE_PRICE_OF_SUPPLIES')}`}
                </Text>
              </FieldSection>
              <FieldSection style={{ position: 'relative', width: 180 }}>
                <Field
                  name="price"
                  component={PriceInput}
                  onChange={setFieldValue}
                  i18n={i18n}
                />
                {errors.price && <Error noMargin>{errors.price}</Error>}
              </FieldSection>
            </>
          )}
          <Actions border>
            <FormSubmitButton dirty={tourStepForm} i18n={i18n} />
          </Actions>
        </FormContainer>
      </Form>
      <OnlineMeetingModal
        handleSubmit={handleModalSubmit}
        experienceUpdating={experienceUpdating}
        onClose={closeModal}
        brandColor={brand.linkColor}
        isOpen={showResourceMeetingModal}
        onlineMeeting={onlineMeeting}
        onlineMeetingVendors={onlineMeetingVendors}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { experienceReducer, initialProps, resource, auth } = state;
  return {
    experience: experienceReducer.experience,
    onlineMeeting: experienceReducer.onlineMeeting,
    onlineMeetingVendors: experienceReducer.onlineMeetingVendors,
    experienceUpdating: experienceReducer.experienceUpdating,
    steps: experienceReducer.steps,
    tourStepForm: experienceReducer.tourStepForm,
    brand: initialProps.brand,
    partner: initialProps.partner,
    showResourceMeetingModal: resource.showResourceMeetingModal,
    user: auth.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      updateTourStepForm,
      setExperienceLength,
      toggleShowResourceMeetingModal,
      createExperienceMeetingInfo,
      updateExperienceMeetingInfo,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DetailsForm);
